import { useEffect, useId, useState } from "react";

import { businessApi } from "../../../../services/api";
import { Button, Dialog, Field, Input, Notice, Select, SkeletonRows, Textarea, cn, plainError } from "../../../../components/ui";

// Report 58 (M2): the owner confirms the business size. Apparify lists the employees it has on the date; the owner adds
// workers Apparify does not know about and may leave someone out only with a reason. The server computes the figure,
// decides every pay-rule consequence and refuses anything invalid; this dialog only collects the owner's statement.

const TYPE_WORD = { full_time: "Full time", part_time: "Part time", contract: "Contract", probationary: "Probationary", intern: "Intern", not_recorded: "Type not recorded" };
const longDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });

/** Field checks before anything is sent; the server checks the same and more. */
export function businessSizeErrors(form, { holidayQuestion = false } = {}) {
  const errors = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.from || ""))) errors.from = "Choose the date these facts apply from.";
  if (!["RETAIL_OR_SERVICE", "OTHER"].includes(form.kind)) errors.kind = "Say whether the business is a retail or service establishment.";
  const outside = String(form.outside ?? "").trim();
  if (!/^\d+$/.test(outside)) errors.outside = "Enter a whole number (0 if everyone is in Apparify).";
  else if (Number(outside) > 0 && String(form.outsideNote || "").trim().length < 10) errors.outsideNote = "Say who they are (at least 10 characters).";
  Object.entries(form.excluded).forEach(([memberId, item]) => {
    if (!item.reason) errors[`reason-${memberId}`] = "Choose why this person is not counted.";
    if (String(item.note || "").trim().length < 20) errors[`note-${memberId}`] = "Explain it in at least 20 characters.";
  });
  const adjusted = Number(outside || 0) > 0 || Object.keys(form.excluded).length > 0;
  const basis = String(form.basis || "").trim();
  if ((adjusted || basis) && basis.length < 20) errors.basis = adjusted ? "Say what you based the change on (at least 20 characters)." : "Write at least 20 characters, or leave it empty.";
  if (holidayQuestion) {
    if (!["PAYS_HOLIDAY_PAY", "LAW_ONLY"].includes(form.practice)) errors.practice = "Choose one.";
    if (String(form.practiceBasis || "").trim().length < 10) errors.practiceBasis = "Say what this is based on (at least 10 characters).";
  }
  if (!form.confirm) errors.confirm = "Tick to confirm these facts.";
  return errors;
}

export default function BusinessSizeDialog({ current, onSaved, onClose }) {
  const formId = useId();
  const [form, setForm] = useState({
    from: "", kind: current?.establishmentKind || "", outside: "0", outsideNote: "", excluded: {}, basis: "", practice: "", practiceBasis: "", confirm: false,
  });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.from)) { setPreview(null); return undefined; }
    const controller = new AbortController();
    setPreview(null);
    setPreviewError("");
    businessApi.getHRWorkforcePreview(form.from, { signal: controller.signal })
      .then((response) => setPreview(response))
      .catch((err) => { if (err?.name !== "CanceledError" && err?.name !== "AbortError") setPreviewError(plainError(err, "Apparify's count couldn't be loaded.")); });
    return () => controller.abort();
  }, [form.from]);

  const set = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((now) => ({ ...now, [key]: value }));
    setErrors((now) => ({ ...now, [key]: undefined }));
    setFailure("");
  };
  const toggleExclude = (person) => setForm((now) => {
    const excluded = { ...now.excluded };
    if (excluded[person.memberId]) delete excluded[person.memberId];
    else excluded[person.memberId] = { reason: "", note: "" };
    return { ...now, excluded };
  });
  const setExclusion = (memberId, key) => (event) => {
    const value = event.target.value;
    setForm((now) => ({ ...now, excluded: { ...now.excluded, [memberId]: { ...now.excluded[memberId], [key]: value } } }));
    setErrors((now) => ({ ...now, [`${key}-${memberId}`]: undefined }));
  };

  const counted = preview?.workforce?.counted || [];
  const excludedCount = Object.keys(form.excluded).length;
  const outsideCount = /^\d+$/.test(String(form.outside).trim()) ? Number(form.outside) : 0;
  const figure = counted.length - excludedCount + outsideCount;
  const holidayRule = (preview?.thresholds || []).find((item) => item.benefit === "HOLIDAY_PAY");
  // The server asks for the holiday answer only when the size places a retail or service business inside the holiday-pay
  // exception; the dialog shows the question at the same point, and the server still decides.
  const holidayQuestion = Boolean(preview && holidayRule && form.kind === "RETAIL_OR_SERVICE"
    && (holidayRule.comparator === "LT" ? figure < holidayRule.threshold : figure <= holidayRule.threshold));

  const submit = async (event) => {
    event.preventDefault();
    if (saving || !preview) return;
    const found = businessSizeErrors(form, { holidayQuestion });
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      const result = await businessApi.recordHREstablishmentFact({
        establishmentKind: form.kind, effectiveFrom: form.from, confirm: true,
        outsideCount: outsideCount, outsideNote: form.outsideNote.trim() || null,
        exclusions: Object.entries(form.excluded).map(([memberId, item]) => ({ memberId: Number(memberId), reason: item.reason, note: item.note.trim() })),
        basis: form.basis.trim() || null,
        holidayPayPractice: holidayQuestion ? form.practice : undefined,
        holidayPracticeBasis: holidayQuestion ? form.practiceBasis.trim() : undefined,
      });
      onSaved(result);
      return;
    } catch (err) {
      setFailure(err?.status === 403 ? "Only the business owner can confirm the business size." : plainError(err, "Not saved. Try again."));
    }
    setSaving(false);
  };

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(open) => { if (!open && !saving) onClose(); }}
      title="Confirm the business size"
      description="Small retail and service businesses are excused from some pay rules. Confirm how many people work for the business and what kind of business it is; payroll uses this from the date you choose."
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving} disabled={!preview}>Confirm business size</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-5">
        <Field label="Applies from" required error={errors.from}
          hint={current ? `Must be after ${longDate(current.effectiveFrom)}, when the current record starts. A payroll already with Finance is never changed.` : "Usually today, or the first day of the next payroll. A payroll already with Finance is never changed."}>
          <Input type="date" value={form.from} onChange={set("from")} disabled={saving} />
        </Field>

        {form.from ? (
          <section aria-label="People Apparify counts" className="space-y-2">
            <h3 className="text-label text-secondary">1. The employees Apparify has on {longDate(form.from)}</h3>
            {previewError ? <Notice tone="danger" role="alert">{previewError}</Notice> : null}
            {!preview && !previewError ? <SkeletonRows rows={3} columns={2} label="Counting the employees" /> : null}
            {preview ? (
              <>
                <p className="text-body-sm text-secondary">
                  Everyone with an employment record on that date is counted, whatever their type of employment. Owners and
                  others with no employment record are not listed. Leave someone out only if they are not really the
                  business&apos;s worker, and say why.
                </p>
                {counted.length ? (
                  <ul className="divide-y divide-subtle rounded-panel border border-default">
                    {counted.map((person) => {
                      const leaving = form.excluded[person.memberId];
                      return (
                        <li key={person.memberId} className="px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-body-sm text-foreground">{person.name} <span className="text-meta text-tertiary">· {TYPE_WORD[person.employmentType || "not_recorded"] || person.employmentType} · since {longDate(person.hireDate)}</span></span>
                            <label className="inline-flex min-h-touch items-center gap-2 text-body-sm text-secondary md:min-h-0">
                              <input type="checkbox" className="h-4 w-4" checked={Boolean(leaving)} onChange={() => toggleExclude(person)} disabled={saving} />
                              Not counted
                            </label>
                          </div>
                          {leaving ? (
                            <div className="mt-2 grid gap-3 md:grid-cols-2">
                              <Field label="Why" required error={errors[`reason-${person.memberId}`]}>
                                <Select value={leaving.reason} onChange={setExclusion(person.memberId, "reason")} disabled={saving}>
                                  <option value="">Choose…</option>
                                  {(preview.exclusionReasons || []).map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
                                </Select>
                              </Field>
                              <Field label="Explanation" required error={errors[`note-${person.memberId}`]} hint="20 to 500 characters. It stays on record.">
                                <Input value={leaving.note} maxLength={500} onChange={setExclusion(person.memberId, "note")} disabled={saving} />
                              </Field>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : <p className="text-body-sm text-secondary">Apparify has no employees with an employment record on that date.</p>}
                {preview.workforce.missingStartDate?.length ? (
                  <Notice tone="warning">
                    {preview.workforce.missingStartDate.map((person) => person.name).join(", ")} {preview.workforce.missingStartDate.length === 1 ? "has" : "have"} no start date, so Apparify cannot count {preview.workforce.missingStartDate.length === 1 ? "them" : "them"}. Add the start date in HR › Employees, or count them below as workers not in Apparify.
                  </Notice>
                ) : null}
              </>
            ) : null}
          </section>
        ) : null}

        {preview ? (
          <>
            <section aria-label="Workers not in Apparify" className="space-y-3">
              <h3 className="text-label text-secondary">2. Workers who are not in Apparify</h3>
              <div className="grid gap-4 md:grid-cols-[10rem_1fr]">
                <Field label="How many" required error={errors.outside}>
                  <Input inputMode="numeric" maxLength={5} value={form.outside} onChange={set("outside")} disabled={saving} className="tabular-nums" />
                </Field>
                <Field label="Who they are" error={errors.outsideNote} hint="For example: the owner and her sister, who work the counter every day.">
                  <Input value={form.outsideNote} maxLength={500} onChange={set("outsideNote")} disabled={saving || outsideCount === 0} />
                </Field>
              </div>
            </section>

            <section aria-label="Kind of business" className="space-y-2">
              <h3 className="text-label text-secondary">3. Is the business a retail or service establishment? <span className="text-danger">*</span></h3>
              <div className="space-y-2" role="radiogroup" aria-label="Is the business a retail or service establishment?">
                {[
                  ["RETAIL_OR_SERVICE", "Yes — retail or service", "It mainly sells goods to people for their own or household use (an apparel shop is retail), or sells services to them."],
                  ["OTHER", "No", "For example it mainly makes garments for other businesses."],
                ].map(([value, label, help]) => (
                  <label key={value} className={cn("flex cursor-pointer gap-3 rounded-panel border px-3 py-2", form.kind === value ? "border-accent bg-accent-subtle" : "border-default")}>
                    <input type="radio" name={`${formId}-kind`} value={value} checked={form.kind === value} onChange={set("kind")} disabled={saving} className="mt-1 h-4 w-4" />
                    <span><span className="block text-body-sm font-medium text-foreground">{label}</span><span className="block text-meta text-secondary">{help}</span></span>
                  </label>
                ))}
              </div>
              {errors.kind ? <p className="text-meta text-danger" role="alert">{errors.kind}</p> : null}
              <p className="text-meta text-tertiary">Definitions: Omnibus Rules Implementing the Labor Code. If you are unsure, ask your accountant.</p>
            </section>

            <div className="rounded-panel border border-default bg-surface-sunken px-3 py-2 text-body-sm">
              <span className="font-medium text-foreground tabular-nums">Business size you are confirming: {figure} worker{figure === 1 ? "" : "s"}</span>
              <span className="block text-meta text-secondary">{counted.length} counted by Apparify{excludedCount ? ` − ${excludedCount} not counted` : ""}{outsideCount ? ` + ${outsideCount} not in Apparify` : ""}.</span>
              {(preview.thresholds || []).length ? (
                <span className="block text-meta text-secondary">Size limits in the law Apparify follows: {preview.thresholds.map((item) => `${item.label.toLowerCase()} — ${item.text} (retail or service only)`).join("; ")}.</span>
              ) : null}
            </div>

            {holidayQuestion ? (
              <section aria-label="Holiday pay" className="space-y-2">
                <h3 className="text-label text-secondary">4. Does the business pay holiday pay anyway? <span className="text-danger">*</span></h3>
                <p className="text-body-sm text-secondary">With this size, the law does not require holiday pay of a retail or service business. Some businesses pay it anyway, by contract, company policy or long practice — then it must keep being paid.</p>
                <div className="space-y-2" role="radiogroup" aria-label="Does the business pay holiday pay anyway?">
                  {[
                    ["PAYS_HOLIDAY_PAY", "Yes, we pay holiday pay", "Payroll keeps paying it under the standard holiday rules."],
                    ["LAW_ONLY", "No, we pay only what the law requires", "Payroll adds nothing for holidays. Check your contracts and past practice first."],
                  ].map(([value, label, help]) => (
                    <label key={value} className={cn("flex cursor-pointer gap-3 rounded-panel border px-3 py-2", form.practice === value ? "border-accent bg-accent-subtle" : "border-default")}>
                      <input type="radio" name={`${formId}-practice`} value={value} checked={form.practice === value} onChange={set("practice")} disabled={saving} className="mt-1 h-4 w-4" />
                      <span><span className="block text-body-sm font-medium text-foreground">{label}</span><span className="block text-meta text-secondary">{help}</span></span>
                    </label>
                  ))}
                </div>
                {errors.practice ? <p className="text-meta text-danger" role="alert">{errors.practice}</p> : null}
                <Field label="What this is based on" required error={errors.practiceBasis} hint="For example: employment contract, clause 7. 10 to 500 characters.">
                  <Input value={form.practiceBasis} maxLength={500} onChange={set("practiceBasis")} disabled={saving} />
                </Field>
              </section>
            ) : null}

            <Field label="What you based this on" error={errors.basis}
              hint={excludedCount || outsideCount ? "Required when you add or leave out people: for example, our payroll list for October. 20 to 1,000 characters." : "Optional. Finance and HR see it."}>
              <Textarea rows={2} maxLength={1000} value={form.basis} onChange={set("basis")} disabled={saving} />
            </Field>

            <label className="flex items-start gap-3 text-body-sm text-foreground">
              <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.confirm} onChange={set("confirm")} disabled={saving} />
              <span>These facts are true for the business from the date above. This is my statement as the owner, not a legal opinion, and the law&apos;s exact way of counting workers may need a qualified adviser.</span>
            </label>
            {errors.confirm ? <p className="text-meta text-danger" role="alert">{errors.confirm}</p> : null}
          </>
        ) : null}
        {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}
